/**
 * Purchase Order — renderer (frontend). Supplies ONLY the body; all shared
 * chrome (company header, title block, footer, print styles) comes from
 * BaseDocumentLayout. This is the template every future document type follows.
 *
 * Reads the snapshot payload produced by backend/documents/types/purchase-order.js.
 */
import type { DocumentProps, FrontendDocumentDefinition } from '../registry';
import { BaseDocumentLayout } from '../BaseDocumentLayout';

function formatCurrency(value: any): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(value: any): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function humanize(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const AMOUNT_KEYS = new Set(['amount', 'total', 'price', 'unit_price', 'cost', 'subtotal']);
const MUTED = 'rgb(var(--gray-500))'; // was #6b7280 — exact match to --gray-500 token

// Self-declared definition — auto-registered by registerDocuments.ts. This is
// the ONLY place Purchase Order is wired on the frontend.
export const documentDefinition: FrontendDocumentDefinition = {
  docType: 'purchase_order',
  renderer: PurchaseOrder,
  appRoute: '/cost-management/purchase-orders',
};

export function PurchaseOrder({ payload }: DocumentProps) {
  const po = payload?.purchase_order || {};
  const company = payload?.company || null;
  const project = payload?.project || null;
  const vendor = payload?.vendor || null;
  const milestones: any[] = Array.isArray(po.milestones) ? po.milestones : [];
  // The form saves milestones as plain strings (one per line); structured milestones (objects) render as a table.
  const structured = milestones.length > 0 && milestones.every((m) => m && typeof m === 'object');
  const columns = structured ? Object.keys(milestones[0]).filter((k) => milestones[0][k] !== undefined) : [];

  return (
    <BaseDocumentLayout
      company={company}
      title="PURCHASE ORDER"
      reference={`#${po.po_number}`}
      meta={[
        { label: 'Status', value: po.status ? humanize(po.status) : null },
        { label: 'Created', value: formatDate(po.created_at) },
        { label: 'Sent', value: formatDate(po.sent_at) },
        { label: 'Complete by', value: formatDate(po.complete_by) },
      ]}
    >
      {/* Vendor + Project */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 20 }}>
        <div style={{ flex: '1 1 240px' }}>
          {/* gap: this field-label style (fontSize 11 / uppercase / marginBottom 4) is
              repeated identically across all 6 document templates — candidate for a
              shared "doc field label" token/class, not added here per scope. */}
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Vendor</div>
          <div style={{ fontWeight: 600 }}>{vendor?.name || 'Vendor'}</div>
          {vendor?.email && <div style={{ color: MUTED }}>{vendor.email}</div>}
          {vendor?.phone && <div style={{ color: MUTED }}>{vendor.phone}</div>}
        </div>
        <div style={{ flex: '1 1 240px' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Project</div>
          <div style={{ fontWeight: 600 }}>{project?.name || '—'}</div>
          {project?.address && <div style={{ color: MUTED }}>{project.address}</div>}
        </div>
      </div>

      {po.title && <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{po.title}</div>}
      {po.description && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 20 }}>{po.description}</div>}

      {milestones.length > 0 && !structured && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Milestones</div>
          <ol style={{ margin: 0, paddingLeft: 20, listStyle: 'decimal' }}>
            {milestones.map((m, i) => (
              <li key={i}>{String(m)}</li>
            ))}
          </ol>
        </div>
      )}

      {structured && (
        <div style={{ overflowX: 'auto', marginBottom: 20 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 480 }}>
            <thead>
              <tr style={{ background: 'rgb(var(--gray-100))', textAlign: 'left' }}>
                {columns.map((col) => (
                  <th key={col} style={{ padding: '8px 10px', borderBottom: '1px solid rgb(var(--gray-300))' }}>{humanize(col)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {milestones.map((m, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgb(var(--gray-200))' }}>
                  {columns.map((col) => (
                    <td key={col} style={{ padding: '8px 10px' }}>
                      {AMOUNT_KEYS.has(col) ? formatCurrency(m[col]) : m[col] == null ? '—' : String(m[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* gap: this footer total block (border-top 2px + paddingTop 12, fontSize 20/700
          value) is repeated identically across 5 of 6 document templates (all but
          Warranty) — candidate for a shared "doc total footer" token/component. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '2px solid rgb(var(--gray-900))', paddingTop: 12 }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED }}>Total</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(po.amount)}</div>
        </div>
      </div>
    </BaseDocumentLayout>
  );
}
